import axios from "axios";

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/api";

let activeRequests = 0;
let loaderHideTimer = null;
const loaderListeners = new Set();

const notifyLoaderListeners = () => {
    const isLoading = activeRequests > 0;
    loaderListeners.forEach((listener) => listener(isLoading));
};

const startLoader = () => {
    if (loaderHideTimer) {
        clearTimeout(loaderHideTimer);
        loaderHideTimer = null;
    }

    activeRequests += 1;
    notifyLoaderListeners();
};

const stopLoader = () => {
    activeRequests = Math.max(0, activeRequests - 1);

    if (activeRequests === 0) {
        loaderHideTimer = setTimeout(() => {
            notifyLoaderListeners();
        }, 180);
        return;
    }

    notifyLoaderListeners();
};

async function computeHmacSha256(secret, message) {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);
    const messageData = encoder.encode(message);

    const cryptoKey = await window.crypto.subtle.importKey(
        "raw",
        keyData,
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"]
    );

    const signatureBuffer = await window.crypto.subtle.sign(
        "HMAC",
        cryptoKey,
        messageData
    );

    const hashArray = Array.from(new Uint8Array(signatureBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        "Content-Type": "application/json",
    },
});

api.interceptors.request.use(
    async (config) => {
        startLoader();

        try {
            const timestamp = Math.floor(Date.now() / 1000).toString();
            const method = (config.method || "GET").toUpperCase();
            const isFormDataPayload =
                typeof FormData !== "undefined" && config.data instanceof FormData;

            // Construct full URL to extract exact path
            let fullUrl = config.url || "";
            if (!fullUrl.startsWith("http://") && !fullUrl.startsWith("https://")) {
                const base = config.baseURL || API_BASE_URL;
                fullUrl = base.replace(/\/$/, "") + "/" + fullUrl.replace(/^\//, "");
            }

            const parsedUrl = new URL(fullUrl);
            const path = parsedUrl.pathname + parsedUrl.search;

            // Let the browser send multipart boundaries for FormData requests.
            if (isFormDataPayload) {
                if (config.headers && typeof config.headers.delete === "function") {
                    config.headers.delete("Content-Type");
                } else if (config.headers) {
                    delete config.headers["Content-Type"];
                }
            }

            // Serialize JSON payloads to ensure exact string match on backend.
            if (
                config.data &&
                typeof config.data === "object" &&
                !isFormDataPayload
            ) {
                config.data = JSON.stringify(config.data);
            }

            const body = isFormDataPayload ? "" : (config.data || "");
            const message = `${timestamp}.${method}.${path}.${body}`;
            const apiKey = process.env.REACT_APP_API_KEY || "REGISTANI#2025@ACHAR!KEY";

            const signature = await computeHmacSha256(apiKey, message);

            config.headers["X-Signature"] = signature;
            config.headers["X-Timestamp"] = timestamp;
        } catch (error) {
            console.error("HMAC signing error:", error);
        }

        return config;
    },
    (error) => {
        stopLoader();
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => {
        stopLoader();
        return response;
    },
    (error) => {
        stopLoader();
        return Promise.reject(error);
    }
);

export const subscribeApiLoading = (listener) => {
    loaderListeners.add(listener);
    listener(activeRequests > 0);

    return () => {
        loaderListeners.delete(listener);
    };
};

// GET Request with sessionStorage cache (TTL: 5 minutes)
export const getData = async (endpoint, params = {}) => {
    const cacheableEndpoints = [
        "/best-seller-products",
        "/trending-products",
        "/latest-products",
        "/products",
        "/sliders",
        "/detect-country"
    ];

    const isCacheable = cacheableEndpoints.some(e => endpoint.startsWith(e));

    if (isCacheable) {
        try {
            const cacheKey = `api_cache_${endpoint}_${JSON.stringify(params)}`;
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
                const { data, timestamp } = JSON.parse(cached);
                if (Date.now() - timestamp < 300000) { // 5 minutes
                    return data;
                }
            }
        } catch (e) {
            console.warn("Cache read error:", e);
        }
    }

    try {
        const response = await api.get(endpoint, { params });
        
        if (isCacheable) {
            try {
                const cacheKey = `api_cache_${endpoint}_${JSON.stringify(params)}`;
                const cacheData = {
                    data: response.data,
                    timestamp: Date.now()
                };
                sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
            } catch (e) {
                console.warn("Cache write error:", e);
            }
        }

        return response.data;
    } catch (error) {
        console.error("GET API Error:", error);
        throw error;
    }
};

// POST Request
export const postData = async (endpoint, data = {}) => {
    try {
        const response = await api.post(endpoint, data);
        return response.data;
    } catch (error) {
        console.error("POST API Error:", error);
        throw error;
    }
};

export const putData = async (endpoint, data = {}) => {
    try {
        const response = await api.put(endpoint, data);
        return response.data;
    } catch (error) {
        console.error("PUT API Error:", error);
        throw error;
    }
};

export const deleteData = async (endpoint) => {
    try {
        const response = await api.delete(endpoint);
        return response.data;
    } catch (error) {
        console.error("DELETE API Error:", error);
        throw error;
    }
};

export default api;
